/**
 * OnboardingModal - 5-step questionnaire for new readers
 * 
 * Steps:
 * 1. welcome_intent - Goals & listening frequency
 * 2. genres - Fiction/nonfiction preferences
 * 3. style_preferences - Session length, pace, story focus
 * 4. experience_expectations - Recommendation intensity, Ask Audia interest
 * 5. email_preferences - Marketing opt-in (default unchecked)
 * 
 * Can be skipped at any point. Responses persist per step.
 */
import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getGenresForBookType } from '@/constants/genreThemes';
import { BACKEND_URL as backendUrl } from '@/config/api';

// Onboarding step configuration (from spec JSON)
const STEPS = [
  {
    step_id: 'welcome_intent',
    order: 1,
    title: 'Welcome to NewAudia',
    subtitle: "Tell us what you're here for so we can tailor your experience.",
    fields: [
      {
        field_id: 'primary_goal',
        type: 'single_select',
        label: "What's your main reason for using NewAudia?",
        options: [
          { value: 'entertainment', label: 'Entertainment' },
          { value: 'learning', label: 'Learning' },
          { value: 'both', label: 'A mix of both' },
          { value: 'exploring', label: 'Just exploring' }
        ]
      },
      {
        field_id: 'listening_frequency',
        type: 'single_select',
        label: 'How often do you listen to audiobooks or spoken content?',
        options: [
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'A few times a week' },
          { value: 'monthly', label: 'A few times a month' },
          { value: 'rarely', label: 'Rarely' }
        ]
      }
    ]
  },
  {
    step_id: 'genres',
    order: 2,
    title: 'Your Genres',
    subtitle: 'Pick what you like. You can change this later.',
    fields: [
      {
        field_id: 'book_type',
        type: 'single_select',
        label: 'What do you gravitate toward?',
        options: [
          { value: 'fiction', label: 'Fiction' },
          { value: 'non-fiction', label: 'Non-Fiction' }
        ]
      },
      {
        field_id: 'genres',
        type: 'dynamic_multi_select',
        label: 'Which genres do you want more of?',
        dependsOn: 'book_type',
        max_selections: 5
      }
    ]
  },
  {
    step_id: 'style_preferences',
    order: 3,
    title: 'Your Listening Style',
    subtitle: 'This helps us pick books that feel right to you.',
    fields: [
      {
        field_id: 'session_length',
        type: 'single_select',
        label: 'Typical listening session length',
        options: [
          { value: '10_20', label: '10–20 minutes' },
          { value: '20_45', label: '20–45 minutes' },
          { value: '45_90', label: '45–90 minutes' },
          { value: '90_plus', label: '90+ minutes' }
        ]
      },
      {
        field_id: 'pace',
        type: 'single_select',
        label: 'What pace do you prefer?',
        options: [
          { value: 'fast', label: 'Fast-paced' },
          { value: 'balanced', label: 'Balanced' },
          { value: 'reflective', label: 'Reflective / slower burn' }
        ]
      },
      {
        field_id: 'story_focus',
        type: 'single_select',
        label: 'What hooks you more?',
        options: [
          { value: 'plot', label: 'Plot-driven' },
          { value: 'character', label: 'Character-driven' },
          { value: 'world', label: 'World-building' },
          { value: 'ideas', label: 'Big ideas / concepts' }
        ]
      }
    ]
  },
  {
    step_id: 'experience_expectations',
    order: 4,
    title: 'Your Experience Preferences',
    subtitle: "We'll use this to personalize your homepage and discovery feed.",
    fields: [
      {
        field_id: 'recommendation_intensity',
        type: 'single_select',
        label: 'How much do you want recommendations?',
        options: [
          { value: 'high', label: 'A lot — keep them coming' },
          { value: 'medium', label: "Some — don't overdo it" },
          { value: 'low', label: "Minimal — I'll explore myself" }
        ]
      },
      {
        field_id: 'referral_source',
        type: 'dropdown',
        label: 'How did you hear about us?',
        options: [
          { value: 'search_engine', label: 'Search Engine (Google, Bing, etc.)' },
          { value: 'social_media', label: 'Social Media (Facebook, Instagram, Twitter/X, etc.)' },
          { value: 'youtube', label: 'YouTube' },
          { value: 'podcast', label: 'Podcast' },
          { value: 'friend_family', label: 'Friend or Family' },
          { value: 'author_referral', label: 'Referred by an Author' },
          { value: 'book_club', label: 'Book Club' },
          { value: 'online_article', label: 'Online Article or Blog' },
          { value: 'email_newsletter', label: 'Email Newsletter' },
          { value: 'advertisement', label: 'Advertisement' },
          { value: 'other', label: 'Other' }
        ]
      }
    ]
  },
  {
    step_id: 'email_preferences',
    order: 5,
    title: 'Stay in the Loop (Optional)',
    subtitle: 'You control what you receive. You can change this later.',
    fields: [
      {
        field_id: 'email_marketing_opt_in',
        type: 'boolean',
        label: 'Yes, email me updates about new books, features, and services.',
        default: false
      }
    ]
  }
];

export default function OnboardingModal() {
  const { showOnboardingModal, setShowOnboardingModal } = useReaderFunnel();
  const { loadCurrentUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  
  // Fetch existing progress when modal opens
  useEffect(() => {
    if (showOnboardingModal) {
      const fetchProgress = async () => {
        setLoadingProgress(true);
        try {
          const token = localStorage.getItem('accessToken');
          if (!token) {
            setCurrentStep(0);
            setResponses({});
            return;
          }
          
          // backendUrl imported from centralized config as BACKEND_URL
          const response = await fetch(`${backendUrl}/api/onboarding/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            const existingPrefs = data.preferences || {};
            
            // Determine which step to resume from based on saved preferences
            let resumeStep = 0;
            
            // Check Step 1 fields
            if (existingPrefs.primary_goal && existingPrefs.listening_frequency) {
              resumeStep = 1; // Move to step 2
            }
            // Check Step 2 fields
            if (existingPrefs.book_type || existingPrefs.fiction_nonfiction || existingPrefs.genres) {
              resumeStep = 2; // Move to step 3
            }
            // Check Step 3 fields
            if (existingPrefs.session_length || existingPrefs.pace || existingPrefs.story_focus) {
              resumeStep = 3; // Move to step 4
            }
            // Check Step 4 fields
            if (existingPrefs.recommendation_intensity || existingPrefs.ask_audia_interest) {
              resumeStep = 4; // Move to step 5
            }
            
            console.log('[ONBOARDING] Resuming from step:', resumeStep, 'existing prefs:', existingPrefs);
            setCurrentStep(resumeStep);
            setResponses(existingPrefs);
          } else {
            setCurrentStep(0);
            setResponses({});
          }
        } catch (error) {
          console.error('[ONBOARDING] Failed to fetch progress:', error);
          setCurrentStep(0);
          setResponses({});
        } finally {
          setLoadingProgress(false);
        }
      };
      
      fetchProgress();
    }
  }, [showOnboardingModal]);
  
  if (!showOnboardingModal) return null;
  
  // Show loading while fetching progress
  if (loadingProgress) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-500 border-t-transparent" />
      </div>
    );
  }
  
  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  
  const saveStepResponse = async (stepId, stepResponses) => {
    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      await fetch(`${backendUrl}/api/onboarding/response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          onboarding_version: 'v1',
          step_id: stepId,
          responses: stepResponses
        })
      });
    } catch (error) {
      console.error('[ONBOARDING] Failed to save response:', error);
    }
  };
  
  const handleNext = async () => {
    setSaving(true);
    
    // Save current step responses
    const stepResponses = {};
    step.fields.forEach(field => {
      if (responses[field.field_id] !== undefined) {
        stepResponses[field.field_id] = responses[field.field_id];
      }
    });
    
    await saveStepResponse(step.step_id, stepResponses);
    
    if (isLastStep) {
      // Mark onboarding as complete
      await saveStepResponse('done', {});
      await loadCurrentUser();
      toast.success('Welcome to NewAudia! 🎉');
      setShowOnboardingModal(false);
    } else {
      setCurrentStep(prev => prev + 1);
    }
    
    setSaving(false);
  };
  
  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleSkip = async () => {
    setSaving(true);
    await saveStepResponse('skip', { reason: 'user_skipped' });
    await loadCurrentUser();
    setShowOnboardingModal(false);
    setSaving(false);
  };
  
  const handleSingleSelect = (fieldId, value) => {
    setResponses(prev => {
      const newState = { ...prev, [fieldId]: value };
      // If book type changes, clear genres selection
      if (fieldId === 'book_type') {
        newState.genres = [];
      }
      return newState;
    });
  };
  
  const handleMultiSelect = (fieldId, value, maxSelections) => {
    setResponses(prev => {
      const current = prev[fieldId] || [];
      const isSelected = current.includes(value);
      
      if (isSelected) {
        return { ...prev, [fieldId]: current.filter(v => v !== value) };
      } else {
        if (maxSelections && current.length >= maxSelections) {
          return prev;
        }
        return { ...prev, [fieldId]: [...current, value] };
      }
    });
  };
  
  const handleBoolean = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };
  
  const renderField = (field) => {
    if (field.type === 'single_select') {
      return (
        <div key={field.field_id} className="space-y-3">
          <label className="block text-sm font-medium text-slate-300">{field.label}</label>
          <div className="grid grid-cols-2 gap-2">
            {field.options.map(option => (
              <button
                key={option.value}
                onClick={() => handleSingleSelect(field.field_id, option.value)}
                className={`p-3 text-sm rounded-lg border transition-all ${
                  responses[field.field_id] === option.value
                    ? 'bg-purple-600/30 border-purple-500 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    
    if (field.type === 'multi_select') {
      const selected = responses[field.field_id] || [];
      return (
        <div key={field.field_id} className="space-y-3">
          <label className="block text-sm font-medium text-slate-300">
            {field.label}
            {field.max_selections && (
              <span className="text-xs text-slate-500 ml-2">(max {field.max_selections})</span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            {field.options.map(option => (
              <button
                key={option.value}
                onClick={() => handleMultiSelect(field.field_id, option.value, field.max_selections)}
                className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                  selected.includes(option.value)
                    ? 'bg-purple-600/30 border-purple-500 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
              >
                {selected.includes(option.value) && <Check className="w-3 h-3 inline mr-1" />}
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    
    if (field.type === 'boolean') {
      const isChecked = responses[field.field_id] ?? field.default ?? false;
      return (
        <div key={field.field_id} className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div 
              onClick={() => handleBoolean(field.field_id, !isChecked)}
              className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${
                isChecked 
                  ? 'bg-purple-600 border-purple-600' 
                  : 'border-slate-600 group-hover:border-slate-500'
              }`}
            >
              {isChecked && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="text-sm text-slate-300">{field.label}</span>
          </label>
        </div>
      );
    }
    
    if (field.type === 'dropdown') {
      const selected = responses[field.field_id] || '';
      return (
        <div key={field.field_id} className="space-y-2">
          <label className="block text-sm font-medium text-slate-300">
            {field.label}
          </label>
          <select
            value={selected}
            onChange={(e) => handleSingleSelect(field.field_id, e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/80 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              backgroundSize: '1.25rem'
            }}
          >
            <option value="" className="bg-slate-800">Select an option...</option>
            {field.options.map(option => (
              <option key={option.value} value={option.value} className="bg-slate-800">
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    
    // Dynamic multi-select that changes options based on another field
    if (field.type === 'dynamic_multi_select') {
      const selected = responses[field.field_id] || [];
      const bookType = responses[field.dependsOn];
      
      // Get genres based on book type selection
      const genreOptions = getGenresForBookType(bookType).filter(g => g.value !== '');
      
      // Don't show if no book type selected
      if (!bookType) {
        return (
          <div key={field.field_id} className="space-y-3">
            <label className="block text-sm font-medium text-slate-400">
              {field.label}
            </label>
            <p className="text-sm text-slate-500 italic">
              Select Fiction or Non-Fiction above to see genre options
            </p>
          </div>
        );
      }
      
      return (
        <div key={field.field_id} className="space-y-3">
          <label className="block text-sm font-medium text-slate-300">
            {field.label}
            {field.max_selections && (
              <span className="text-xs text-slate-500 ml-2">(select up to {field.max_selections})</span>
            )}
          </label>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-2">
            {genreOptions.map(option => (
              <button
                key={option.value}
                onClick={() => handleMultiSelect(field.field_id, option.value, field.max_selections)}
                className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                  selected.includes(option.value)
                    ? 'bg-purple-600/30 border-purple-500 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
              >
                {selected.includes(option.value) && <Check className="w-3 h-3 inline mr-1" />}
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8"
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-slate-700">
          {/* NewAudia Logo */}
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-10 mx-auto mb-3 rounded-lg"
          />
          
          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mb-4">
            {STEPS.map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'w-6 bg-purple-500' 
                    : idx < currentStep 
                      ? 'bg-purple-600' 
                      : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-xs text-purple-400 font-medium">Step {currentStep + 1} of {STEPS.length}</span>
            </div>
            <h2 className="text-xl font-bold text-white">{step.title}</h2>
            <p className="text-sm text-slate-400 mt-1">{step.subtitle}</p>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {step.fields.map(field => renderField(field))}
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <div>
            {!isFirstStep && (
              <Button
                onClick={handleBack}
                variant="ghost"
                className="text-slate-400 hover:text-slate-300"
                disabled={saving}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="text-slate-500 hover:text-slate-400"
              disabled={saving}
            >
              Skip for now
            </Button>
            <Button
              onClick={handleNext}
              className="bg-purple-600 hover:bg-purple-500 text-white"
              disabled={saving}
            >
              {saving ? 'Saving...' : isLastStep ? 'Finish' : 'Continue'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
